import React, { useMemo } from 'react';
import { BsTrash } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Divider, Flex, HStack, Icon, IconButton, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { generateS3GetUrl } from '../../../redux/modules/documents';
import { hideModal, showModal } from '../../../redux/modules/modal';
import DeleteModalBody from '../../DeleteModalBody';
import LoadingSpinner from '../../Spinner';

import EditPolicyModal from './EditPolicyModal';
import InsurancePolicyCard from './InsurancePolicyCard';
import RequirementBlock from './RequirementBlock';

const PreviousSubmissionsBlock = ({ blueprint }) => {
  const dispatch = useDispatch();
  const userPolicies = useSelector(blueprint.selectUserPolicies);
  const userDocuments = useSelector(blueprint.selectUserDocuments);
  const company = useSelector(blueprint.selectCompany);
  const policiesLoading = useSelector(blueprint.selectPoliciesLoading);
  const documentsLoading = useSelector(blueprint.selectDocumentsLoading);
  const { companyId } = company;

  const submissionItemCount = useMemo(
    () => userPolicies.length + userDocuments.length,
    [userPolicies, userDocuments]
  );

  const handleDocClick = async docId => {
    const getUrl = await dispatch(generateS3GetUrl(docId));
    window.open(getUrl, '_blank');
  };

  const handleDocDeleteClick = doc => {
    dispatch(showModal(() => (
      <DeleteModalBody
        deleteMessage="Are you sure you want to delete this document?"
        itemIdKey="id"
        itemLabelKey="displayName"
        itemsToDelete={[doc]}
        primaryOnClick={async () => {
          const onSuccess = () => dispatch(hideModal());

          dispatch(blueprint.deleteDocument(doc.id, true, onSuccess));
        }}
      />
    ), {
      title: 'Delete Document?',
      size: 'sm'
    }));
  };

  const handlePolicyClick = (policyType, currentPolicy) => {
    dispatch(showModal(() => (
      <EditPolicyModal
        blueprint={blueprint}
        cancelFunction={() => dispatch(hideModal())}
        company={company}
        policy={currentPolicy}
        policyType={policyType}
        submitFunction={data => {
          dispatch(blueprint.updatePolicy(currentPolicy.id, data));
          dispatch(hideModal());
        }}
      />
    ), { size: '3xl', title: 'Edit Policy' }));
  };

  return (
    <Flex flexBasis="100%" mt={8}>
      <RequirementBlock title={`Your Submissions: (${submissionItemCount} Items)`}>
        <Flex alignItems="flex-start" flexFlow="row nowrap" justifyContent="flex-start">
          <Box minW="350px" pr={12}>
            <Text mb={2} textStyle="bodyXl">Documents</Text>
            { documentsLoading
              ? (
                <Box
                  alignItems="center"
                  h="200px"
                  justifyContent="center"
                  p={12}
                  w="200px"
                >
                  <LoadingSpinner isInline />
                </Box>
              )
              : (
                <>
                  { userDocuments.map(doc => (
                    <Box
                      bgColor="greyScale.200"
                      borderRadius="md"
                      key={doc.id}
                      my={2}
                      p={4}
                    >
                      <HStack justifyContent="space-between">
                        <Text
                          _hover={{ color: 'primaryScale.600', cursor: 'pointer' }}
                          overflow="hidden"
                          textOverflow="ellipsis"
                          textStyle="button"
                          whiteSpace="nowrap"
                          onClick={() => handleDocClick(doc.id)}
                        >
                          {doc.displayName}
                        </Text>
                        {/* <IconButton */}
                        {/*   icon={<Icon as={BsTrash} />} */}
                        {/*   size="sm" */}
                        {/*   variant="textBlack" */}
                        {/*   onClick={() => handleDocDeleteClick(doc)} */}
                        {/* /> */}
                      </HStack>
                    </Box>
                  )) }
                </>
              )}
          </Box>

          <Box>
            <Text mb={2} textStyle="bodyXl">Policies</Text>
            <Flex flexFlow="row wrap">
              {policiesLoading
                ? (
                  <Box
                    alignItems="center"
                    h="200px"
                    justifyContent="center"
                    p={12}
                    w="200px"
                  >
                    <LoadingSpinner isInline />
                  </Box>
                )
                : (
                  <>
                    { userPolicies.map(policy => (
                      <Box
                        key={policy?.id}
                        minW="500px"
                        mx={2}
                        onClick={() => handlePolicyClick(policy.insurancePolicyType, policy)}
                      >
                        <InsurancePolicyCard companyId={companyId} policy={policy} />
                      </Box>
                    )) }
                  </>
                )}
            </Flex>
          </Box>
        </Flex>
      </RequirementBlock>
    </Flex>
  );
};

PreviousSubmissionsBlock.propTypes = { bluePrint: PropTypes.shape({}) };

export default PreviousSubmissionsBlock;
